import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import styled from 'styled-components';
import theme from 'theme';

import { yupResolver } from '@hookform/resolvers/yup';
import Error from 'components/actionModal/Error';
import ValidatedDropdown from 'components/actionModal/forms/inputs/ValidatedDropdown';
import UploadOfferPageField from 'components/actionModal/forms/uploadOfferPageField/UploadOfferPageField';
import {
  fileDropText,
  logoExtensions,
  logoFileExtensionsForDrop,
  maxFileSizeText,
} from 'components/actionModal/models/uploadConfig';
import DefaultButton from 'components/buttons/DefaultButton';
import { businessSectors } from 'components/listForSelectingData/dataForLists';
import {
  CorporateAccountDetailsData,
  CreateCorporateAccountPayload,
} from 'modules/accounts/accountsServices/CorporateAccountsService';
import {
  CorporateAccountFormValues,
  CorporateAccountMutationError,
} from 'modules/accounts/models';

import StandardInput from '../../forms/inputs/StandardInput';
import { InputId } from '../../models';
import { corporateAccountDetailsValidationSchema } from '../../models/validationSchemas';
import {
  FormContainer,
  SubmitButtonContainer,
} from '../../styledElements/formsStyledElements';
import { Title } from '../../styledElements/stagesStyledElements';

type CorporateAccountDetailsProps = {
  title: string;
  groupName: CorporateAccountDetailsData['companyName'];
  businessSector: CorporateAccountDetailsData['businessSector'];
  errorResponse?: CorporateAccountMutationError;
  logoImage?: string;
  logoName?: string;
  goToNextStage: (data: CreateCorporateAccountPayload) => void;
  goToPreviousStage?: () => void;
  toggleNextStage?: (value?: boolean) => void;
};

const CorporateAccountDetails = ({
  title,
  groupName,
  businessSector,
  errorResponse,
  logoImage: initialLogoImage = '',
  logoName: initialLogoName = '',
  goToNextStage,
  goToPreviousStage,
  toggleNextStage,
}: CorporateAccountDetailsProps) => {
  const {
    register,
    control,
    setValue,
    handleSubmit,
    setError,
    setFocus,
    formState: { isValid, errors },
  } = useForm<CorporateAccountFormValues>({
    resolver: yupResolver(corporateAccountDetailsValidationSchema),
  });

  const [logoImage, setLogoImage] = useState(initialLogoImage);
  const [logoName, setLogoName] = useState(initialLogoName);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoError, setLogoError] = useState(false);
  const [errorText, setErrorText] = useState('');

  const formSubmit: SubmitHandler<CorporateAccountFormValues> = (data) => {
    const payload = {
      groupName: data[InputId.CompanyNameInputId],
      businessSector: data[InputId.BusinessSectorInputId],
      logoFile: logoFile,
    };

    goToNextStage(payload);
  };

  const setFileLogoHandler = (
    url: string,
    _textWithPercentages?: HTMLParagraphElement | null,
    file?: File
  ) => {
    setLogoImage(url);
    setLogoFile(file ?? null);

    if (!url) {
      setLogoName('');
      setLogoFile(null);
    }
  };

  useEffect(() => {
    if (!errorResponse) return;
    const { input, message } = errorResponse;

    if (input) {
      setError(input, { message });
      setFocus(input);
    }
  }, [errorResponse, setError, setFocus]);

  useEffect(() => {
    if (toggleNextStage) {
      toggleNextStage(!isValid);
    }
  }, [isValid, toggleNextStage]);

  return (
    <div>
      <Title>{title}</Title>
      <FormContainer onSubmit={handleSubmit(formSubmit)}>
        <StandardInput
          errors={errors}
          register={register}
          setValue={setValue}
          label="Group name"
          inputId={InputId.CompanyNameInputId}
          inputValue={groupName}
        />
        <ValidatedDropdown
          inputId={InputId.BusinessSectorInputId}
          listLabel="Business Sector"
          inputLabel="Business Sector"
          inputValue={businessSector ?? ''}
          list={businessSectors}
          control={control}
          errors={errors}
        />
        <UploadWrapper>
          <UploadOfferPageField
            pictureDescription={maxFileSizeText}
            fileDropZoneText={fileDropText}
            fileName={logoName}
            file={logoImage}
            fileError={logoError}
            validExtensions={logoExtensions}
            validExtensionsForDrop={logoFileExtensionsForDrop}
            setFileError={setLogoError}
            uploadSuggestionText="Upload logo"
            uploadSuggestionFormat="(.SVG .PNG)"
            setErrorText={setErrorText}
            setFileName={setLogoName}
            setFile={setFileLogoHandler}
          />
        </UploadWrapper>
        <SubmitButtonContainer>
          {goToPreviousStage && (
            <DefaultButton
              text="back"
              textColor={theme.colors.white}
              isActive
              withShadow
              buttonColor={theme.colors.funtainBlue}
              isUppercase
              buttonSize="md"
              borderSize="md"
              clickHandler={goToPreviousStage}
            />
          )}
          <DefaultButton
            text="create account"
            textColor={theme.colors.white}
            isActive={isValid}
            isSubmit
            withShadow
            buttonColor={
              isValid ? theme.colors.funtainBlue : theme.colors.quillGray
            }
            isUppercase
            buttonSize="md"
            borderSize="md"
          />
        </SubmitButtonContainer>
      </FormContainer>
      <Error isOpen={logoError} errorText={errorText} />
    </div>
  );
};

const UploadWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;

  & > * {
    width: 100%;
    align-items: flex-start;
  }
`;

export default CorporateAccountDetails;
